import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { useCreateWidgetMutation, useGetAccountQuery } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useEditorContext } from '../SMLEditorContext';
import { newWidgetUUID } from '../blocks/newWidgetUUID';
import { defaultBlockData } from './defaultBlockData';
import { pickerMessages } from './pickerMessages';
import { useEditorSongs } from './useEditorSongs';

export interface AddBlockRequest {
  type: SmartLinkWidgetType;
  /** The block above the new one; null puts it at the end. */
  afterUUID: string | null;
  /** The song of a Catalog links block (design c6). */
  catalogItemUuid?: string;
}

/**
 * Adds a block at once (design b1): `createWidget` with `position` after the block above, then its
 * row opens. Returns the new uuid, or null when the block could not be added (the toast says why).
 */
export function useAddBlock() {
  const intl = useIntl();
  const { smartLink, accountId, smartLinkId, url, showNotice, saves } = useEditorContext();
  const [createWidget] = useCreateWidgetMutation();
  const songs = useEditorSongs(accountId, smartLinkId);
  const { data: account } = useGetAccountQuery({ accountId });
  // Several adds in a row ("Add all three") each read the page of the answer before.
  const widgetsRef = useRef<readonly SerializedWidget[]>(smartLink.recentSmartLinkVersion.widgets ?? []);
  widgetsRef.current = smartLink.recentSmartLinkVersion.widgets ?? widgetsRef.current;

  const add = useCallback(
    async ({ type, afterUUID, catalogItemUuid }: AddBlockRequest, open = true): Promise<string | null> => {
      const made = defaultBlockData(
        intl,
        type,
        {
          song: songs.pageSong ?? songs.newestSong,
          songs: songs.items,
          accountUrl: account?.account.shareUrl ?? null,
        },
        catalogItemUuid
      );
      if ('error' in made) {
        showNotice(made.error);
        return null;
      }
      const before = widgetsRef.current;
      const after = afterUUID === null ? undefined : before.find((widget) => widget.uuid === afterUUID);
      const result = await saves.track(
        createWidget({
          accountId,
          smartLinkId,
          type,
          data: made.data,
          position: after === undefined ? undefined : after.position + 1,
        }),
        (answer) => !isSuccessfulResult(answer)
      );
      if (!isSuccessfulResult(result)) {
        showNotice(intl.formatMessage(pickerMessages.addFailed));
        return null;
      }
      const widgets = result.data.smartLink.recentSmartLinkVersion.widgets ?? [];
      widgetsRef.current = widgets;
      const uuid = newWidgetUUID(before, widgets);
      if (open && uuid !== null) {
        url.openBlock(uuid);
      }
      return uuid;
    },
    [account, accountId, createWidget, intl, saves, showNotice, smartLinkId, songs, url]
  );

  return { add, songs };
}
